import React, { useMemo } from 'react';
import Select from 'react-select';
import ToggleOff from '@mui/icons-material/ToggleOffOutlined';
import ToggleOn from '@mui/icons-material/ToggleOn';
import './EnvironmentalStatusSettings.css';
import ColorPicker from './ColorPicker';
import LabelColorSlider from './LabelColorSlider';
import {
  ENVIRONMENTAL_STATUS_ICON_TYPES,
  createEmptyEnvironmentalStatusMetric,
  inferEnvironmentalMetricKind,
} from '../widgets/environmentalStatusConfig';
import { DeleteIcon } from '../svg/UtilsIcons';

const toggleStyle = { color: 'var(--accent)', width: '48px', height: '40px', cursor: 'pointer' };

const COLOR_PARTS = [
  { key: 'labelBackground', label: 'Title BG' },
  { key: 'label', label: 'Title Text' },
  { key: 'value', label: 'Value' },
  { key: 'unit', label: 'Unit' },
  { key: 'iconBackground', label: 'Icon BG' },
  { key: 'icon', label: 'Icon' },
];

const getMetricBounds = (datastream) => {
  const min = Number(datastream?.min);
  const max = Number(datastream?.max);
  return {
    min: Number.isFinite(min) ? min : 0,
    max: Number.isFinite(max) ? max : 100,
  };
};

const EnvironmentalStatusSettings = ({
  edit,
  setEdit,
  devices = [],
  validDatastreams = [],
  deviceOptions = [],
}) => {
  const props = edit?.props || {};
  const metrics = Array.isArray(props.metrics) ? props.metrics : [];
  const hasDevices = Array.isArray(devices) && devices.length > 0;
  const controlDatastream = props.controlDatastream || {};
  const showControlSwitch =
    typeof props.showControlSwitch === 'boolean' ? props.showControlSwitch : true;

  const isControlledDatastream = (datastream) => {
    if (!datastream || typeof datastream !== 'object') return false;

    const directControlValue =
      datastream.isControlled ??
      datastream.controlled ??
      datastream.is_controlled ??
      datastream.canControl ??
      datastream.can_control ??
      datastream.writable ??
      datastream.isWritable ??
      datastream.is_writable;

    if (typeof directControlValue === 'boolean') return directControlValue;
    if (typeof directControlValue === 'number') return directControlValue !== 0;
    if (typeof directControlValue === 'string') {
      const normalizedValue = directControlValue.trim().toLowerCase();
      if (['true', '1', 'yes', 'on', 'write', 'writable', 'control', 'controlled'].includes(normalizedValue)) {
        return true;
      }
      if (['false', '0', 'no', 'off', 'read', 'readonly', 'read-only'].includes(normalizedValue)) {
        return false;
      }
    }

    const semanticFields = [
      datastream.type,
      datastream.dataType,
      datastream.data_type,
      datastream.widgetType,
      datastream.io,
      datastream.ioType,
      datastream.direction,
      datastream.access,
      datastream.category,
      datastream.name,
      datastream.pin,
    ]
      .map((value) => String(value || '').trim().toLowerCase())
      .filter(Boolean);

    return semanticFields.some((value) => (
      value.includes('control') ||
      value.includes('switch') ||
      value.includes('relay') ||
      value.includes('output') ||
      value === 'rw' ||
      value === 'write'
    ));
  };

  const resolvedDeviceOptions = useMemo(() => {
    if (Array.isArray(deviceOptions) && deviceOptions.length > 0) {
      return deviceOptions;
    }

    return (devices || []).map((device) => ({
      value: Number(device?.id) || 0,
      label: device?.name || `Device ${device?.id ?? ''}`,
    }));
  }, [deviceOptions, devices]);

  const updateProps = (patch) =>
    setEdit((prev) => ({
      ...prev,
      props: {
        ...(prev.props || {}),
        ...patch,
      },
    }));

  const updateMetric = (metricId, patch) =>
    setEdit((prev) => ({
      ...prev,
      props: {
        ...(prev.props || {}),
        metrics: ((prev.props?.metrics || [])).map((metric) =>
          metric?.id === metricId
            ? {
                ...metric,
                ...patch,
              }
            : metric
        ),
      },
    }));

  const removeMetric = (metricId) =>
    setEdit((prev) => ({
      ...prev,
      props: {
        ...(prev.props || {}),
        metrics: ((prev.props?.metrics || [])).filter((metric) => metric?.id !== metricId),
      },
    }));

  const getDatastreamList = (metric) => {
    if (!hasDevices) return (validDatastreams || []).filter(Boolean);

    const streamDevice = (devices || []).find(
      (device) => Number(device?.id) === Number(metric?.deviceId)
    );

    return (streamDevice?.template?.datastreams || []).filter(Boolean);
  };

  const getDatastreamOptions = (metric) =>
    getDatastreamList(metric).map((datastream) => ({
      value: Number(datastream?.id),
      label: `${datastream?.name || 'Datastream'} [${datastream?.pin || '-'}]`,
    }));

  const getControlledDatastreamList = (metric) =>
    getDatastreamList(metric).filter(isControlledDatastream);

  const getControlledDatastreamOptions = (metric) =>
    getControlledDatastreamList(metric).map((datastream) => ({
      value: Number(datastream?.id),
      label: `${datastream?.name || 'Controlled Datastream'} [${datastream?.pin || '-'}]`,
    }));

  const controlledDatastreamOptions = getControlledDatastreamOptions(controlDatastream);

  return (
    <div className="ess-settings">
      <button
        type="button"
        className="details-add-btn"
        style={{ width: '220px' }}
        onClick={() => {
          updateProps({
            metrics: [...metrics, createEmptyEnvironmentalStatusMetric(metrics.length)],
          });
        }}
      >
        <span>+</span>
        <span>Add Datastream</span>
      </button>

      <div className="ess-grid">
        {metrics.map((metric, index) => {
          const datastreamOptions = getDatastreamOptions(metric);
          const isEnabled = typeof metric?.enabled === 'boolean' ? metric.enabled : true;
          const caption =
            metric?.iconType === 'auto' || !metric?.iconType
              ? inferEnvironmentalMetricKind(metric?.label, metric?.unit).toUpperCase()
              : String(metric.iconType).toUpperCase();

          return (
            <div
              key={metric?.id || `metric-${index}`}
              className={`ess-card ${isEnabled ? 'is-enabled' : 'is-disabled'}`}
            >
              <div className="ess-cardHeader">
                <div className="ess-cardHeading">
                  <span className="ess-cardTitle">
                    {metric?.label || `Datastream ${index + 1}`}
                  </span>
                  <span className="ess-cardCaption">{caption}</span>
                </div>

                <div className="ess-cardActions">
                  <button
                    type="button"
                    className={`ess-toggle ${isEnabled ? 'is-on' : ''}`}
                    aria-pressed={isEnabled}
                    onClick={() => updateMetric(metric.id, { enabled: !isEnabled })}
                  >
                    <span className="ess-toggleThumb" />
                  </button>

                  <button
                    type="button"
                    className="details-delete-btn ess-deleteBtn"
                    style={{ position: 'static' }}
                    onClick={() => removeMetric(metric.id)}
                  >
                    <DeleteIcon />
                  </button>
                </div>
              </div>

              <div className="ess-cardBody">
                <div className="form-item ess-formItem">
                  <label className="form-lbl ess-formLabel">Label</label>
                  <input
                    type="text"
                    className="add-column"
                    value={metric?.label || ''}
                    onChange={(e) => updateMetric(metric.id, { label: e.target.value })}
                  />
                </div>

                <div className="form-item ess-formItem">
                  <label className="form-lbl ess-formLabel">Icon</label>
                  <Select
                    classNamePrefix="react-select"
                    options={ENVIRONMENTAL_STATUS_ICON_TYPES}
                    value={ENVIRONMENTAL_STATUS_ICON_TYPES.find(
                      (option) => option.value === (metric?.iconType || 'auto')
                    )}
                    onChange={(selectedOption) =>
                      updateMetric(metric.id, { iconType: selectedOption?.value || 'auto' })
                    }
                    isClearable={false}
                  />
                </div>

                {hasDevices ? (
                  <div className="form-item ess-formItem">
                    <label className="form-lbl ess-formLabel">Device</label>
                    <Select
                      classNamePrefix="react-select"
                      options={resolvedDeviceOptions}
                      value={
                        resolvedDeviceOptions.find(
                          (option) => Number(option.value) === Number(metric?.deviceId)
                        ) || null
                      }
                      onChange={(selectedOption) =>
                        updateMetric(metric.id, {
                          deviceId: selectedOption ? Number(selectedOption.value) : 0,
                          dataId: null,
                          pin: '',
                        })
                      }
                      isClearable={true}
                    />
                  </div>
                ) : null}

                <div className="form-item ess-formItem">
                  <label className="form-lbl ess-formLabel">Datastream</label>
                  <Select
                    classNamePrefix="react-select"
                    options={datastreamOptions}
                    value={
                      datastreamOptions.find(
                        (option) => Number(option.value) === Number(metric?.dataId)
                      ) || null
                    }
                    onChange={(selectedOption) => {
                      const selectedDatastream = getDatastreamList(metric).find(
                        (datastream) => Number(datastream?.id) === Number(selectedOption?.value)
                      );

                      updateMetric(metric.id, {
                        dataId: selectedOption ? Number(selectedOption.value) : null,
                        pin: selectedDatastream?.pin || '',
                        label: selectedDatastream?.name || metric?.label || '',
                        unit:
                          selectedDatastream?.unit !== undefined &&
                          selectedDatastream?.unit !== null &&
                          String(selectedDatastream.unit).trim() !== ''
                            ? selectedDatastream.unit
                            : metric?.unit || '',
                        defaultValue:
                          selectedDatastream?.defaultValue !== undefined
                            ? selectedDatastream.defaultValue
                            : metric?.defaultValue ?? '',
                      });
                    }}
                    isDisabled={hasDevices && !metric?.deviceId}
                    isClearable={true}
                  />
                </div>

                <div className="form-item ess-formItem">
                  <label className="form-lbl ess-formLabel">Default Value</label>
                  <input
                    type="text"
                    className="add-column"
                    value={metric?.defaultValue ?? ''}
                    onChange={(e) => updateMetric(metric.id, { defaultValue: e.target.value })}
                  />
                </div>

                <div className="form-item ess-formItem">
                  <label className="form-lbl ess-formLabel">Unit</label>
                  <input
                    type="text"
                    className="add-column"
                    value={metric?.unit || ''}
                  onChange={(e) => updateMetric(metric.id, { unit: e.target.value })}
                  />
                </div>

                <div className="form-item ess-formItem">
                  <label className="form-lbl ess-formLabel">Decimals</label>
                  <input
                    type="number"
                    className="add-column"
                    min={0}
                    max={6}
                    value={Number(metric?.decimals ?? 0)}
                    onChange={(e) =>
                      updateMetric(metric.id, {
                        decimals: Math.max(0, Number(e.target.value || 0)),
                      })
                    }
                  />
                </div>

                <div className="ess-colorPanel">
                  <span className="ess-colorPanelTitle">Colors</span>
                  {COLOR_PARTS.map((part) => {
                    const datastream = getDatastreamList(metric).find(
                      (item) => Number(item?.id) === Number(metric?.dataId)
                    );
                    const bounds = getMetricBounds(datastream);
                    const fixedColorKey = `${part.key}Color`;
                    const changeKey = `${part.key}ChangeColorBasedOnValue`;
                    const gradingKey = `${part.key}ColorGrading`;
                    const colorsKey = `${part.key}Colors`;
                    const changeEnabled = metric?.[changeKey] === true;

                    return (
                      <div className="ess-colorBlock" key={`${metric.id}-${part.key}`}>
                        <div className="ess-colorBlockHead">
                          <span>{part.label}</span>
                          <button
                            type="button"
                            className={`ess-toggle ${changeEnabled ? 'is-on' : ''}`}
                            aria-pressed={changeEnabled}
                            onClick={() => updateMetric(metric.id, { [changeKey]: !changeEnabled })}
                          >
                            <span className="ess-toggleThumb" />
                          </button>
                        </div>
                        {changeEnabled ? (
                          <>
                            <div className="ess-colorGradientToggle">
                              {metric?.[gradingKey] !== false ? (
                                <ToggleOn
                                  title="Smooth gradient transition"
                                  style={toggleStyle}
                                  onClick={() => updateMetric(metric.id, { [gradingKey]: false })}
                                />
                              ) : (
                                <ToggleOff
                                  title="Smooth gradient transition"
                                  style={toggleStyle}
                                  onClick={() => updateMetric(metric.id, { [gradingKey]: true })}
                                />
                              )}
                            </div>
                            <LabelColorSlider
                              minValue={bounds.min}
                              maxValue={bounds.max}
                              stops={metric?.[colorsKey] || []}
                              colorGrading={metric?.[gradingKey] !== false}
                              setStops={(updatedStops) => updateMetric(metric.id, { [colorsKey]: updatedStops })}
                            />
                          </>
                        ) : (
                          <ColorPicker
                            id={`environmental-${metric.id}-${part.key}-color`}
                            color={metric?.[fixedColorKey] || '#00bcd4'}
                            setColor={(color) => updateMetric(metric.id, { [fixedColorKey]: color })}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="ess-card ess-controlCard">
        <div className="ess-cardHeader">
          <div className="ess-cardHeading">
            <span className="ess-cardTitle">Control Switch</span>
            <span className="ess-cardCaption">Link the switch to any controlled datastream.</span>
          </div>

          <button
            type="button"
            className={`ess-toggle ${showControlSwitch ? 'is-on' : ''}`}
            aria-pressed={showControlSwitch}
            onClick={() => updateProps({ showControlSwitch: !showControlSwitch })}
          >
            <span className="ess-toggleThumb" />
          </button>
        </div>

        <div className="ess-controlGrid">
          {hasDevices ? (
            <div className="form-item ess-formItem">
              <label className="form-lbl ess-formLabel">Control Device</label>
              <Select
                classNamePrefix="react-select"
                options={resolvedDeviceOptions}
                value={
                  resolvedDeviceOptions.find(
                    (option) => Number(option.value) === Number(controlDatastream?.deviceId)
                  ) || null
                }
                onChange={(selectedOption) =>
                  updateProps({
                    controlDatastream: {
                      deviceId: selectedOption ? Number(selectedOption.value) : 0,
                      dataId: null,
                      pin: '',
                    },
                  })
                }
                isClearable={true}
              />
            </div>
          ) : null}

          <div className="form-item ess-formItem">
            <label className="form-lbl ess-formLabel">Controlled Datastream</label>
            <Select
              classNamePrefix="react-select"
              options={controlledDatastreamOptions}
              value={
                controlledDatastreamOptions.find(
                  (option) => Number(option.value) === Number(controlDatastream?.dataId)
                ) || null
              }
              onChange={(selectedOption) => {
                const selectedDatastream = getControlledDatastreamList(controlDatastream).find(
                  (datastream) => Number(datastream?.id) === Number(selectedOption?.value)
                );

                updateProps({
                  controlDatastream: {
                    ...(controlDatastream || {}),
                    dataId: selectedOption ? Number(selectedOption.value) : null,
                    pin: selectedDatastream?.pin || '',
                    defaultValue:
                      selectedDatastream?.defaultValue !== undefined
                        ? selectedDatastream.defaultValue
                        : controlDatastream?.defaultValue ?? 0,
                  },
                });
              }}
              isDisabled={hasDevices && !controlDatastream?.deviceId}
              isClearable={true}
              placeholder="Select controlled datastream"
            />
          </div>
        </div>
      </div>

      <div className="ess-sizeGrid">
        <div className="form-item ess-formItem">
          <label className="form-lbl ess-formLabel">Title Color</label>
          <ColorPicker
            id="environmental-status-title-color"
            color={props.titleColor || '#ffffff'}
            setColor={(titleColor) => updateProps({ titleColor })}
          />
        </div>

        <div className="form-item ess-formItem">
          <label className="form-lbl ess-formLabel">Title Size</label>
          <input
            type="number"
            className="add-column"
            min={12}
            max={48}
            value={Number(props.titleSize ?? 20)}
            onChange={(e) => updateProps({ titleSize: Number(e.target.value || 20) })}
          />
        </div>

        <div className="form-item ess-formItem">
          <label className="form-lbl ess-formLabel">Datastream Title Size</label>
          <input
            type="number"
            className="add-column"
            min={10}
            max={32}
            value={Number(props.datastreamTitleSize ?? 14)}
            onChange={(e) => updateProps({ datastreamTitleSize: Number(e.target.value || 14) })}
          />
        </div>

        <div className="form-item ess-formItem">
          <label className="form-lbl ess-formLabel">Value Size</label>
          <input
            type="number"
            className="add-column"
            min={24}
            max={120}
            value={Number(props.valueSize ?? 72)}
            onChange={(e) => updateProps({ valueSize: Number(e.target.value || 72) })}
          />
        </div>

        <div className="form-item ess-formItem">
          <label className="form-lbl ess-formLabel">Border Radius</label>
          <input
            type="number"
            className="add-column"
            min={0}
            max={48}
            value={Number(props.borderRadius ?? 24)}
            onChange={(e) => updateProps({ borderRadius: Number(e.target.value || 0) })}
          />
        </div>
      </div>
    </div>
  );
};

export default EnvironmentalStatusSettings;
